import React, { useCallback, useEffect, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { Translate, useCommonTranslation } from 'c-translation';
import { PermissionName } from 'c-sdk';
import type { NotificationReport } from 'c-entity-types';
import ReactGA from 'react-ga';
import { notificationsIcon } from 'c-main/icons';
import { ControlledFormInput, CreateEntityButton, TextField } from 'c-components';
import type { EntityListColumn, RenderColumnDataProps } from 'c-pagination';
import {
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    generateColumn,
} from 'c-pagination';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import type { CreateNotificationFormValues } from './notificationsListLib';
import {
    createSchema,
    formatDate,
    formatRecipients,
    getValidEmailCount as countValidEmails,
    transformCreateValues as toCreateRequest,
} from './notificationsListLib';

const paginationTag = 'notifications-list-search';

const textSearchCols = ['name'];

const statusPillSx = (isPaused: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    border: '1px solid',
    borderColor: isPaused ? 'warning.light' : 'success.light',
    borderRadius: 1,
    color: isPaused ? 'warning.dark' : 'success.dark',
    bgcolor: isPaused ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.08)',
    lineHeight: 1.4,
});

const StatusCell: React.FC<RenderColumnDataProps<NotificationReport>> = ({ entity }) => {
    const t = useCommonTranslation();
    const isPaused = Boolean(entity.is_paused);

    return (
        <Typography variant="caption" sx={statusPillSx(isPaused)}>
            {isPaused
                ? t('Modules.Admin.NotificationsList.table.paused')
                : t('Modules.Admin.NotificationsList.table.active')}
        </Typography>
    );
};

const NotificationsListPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const createDefaults = useMemo<CreateNotificationFormValues>(
        () => ({
            name: '',
            interval: '',
            email_subject: '',
            email_body: '',
            email_addresses: '',
        }),
        [],
    );

    const getValidEmailCount = useCallback(countValidEmails, []);

    const transformCreateValues = useCallback(toCreateRequest, []);

    const columns = useMemo<EntityListColumn<NotificationReport>[]>(
        () => [
            generateColumn<NotificationReport>({
                headerTitle: 'Modules.Admin.NotificationsList.table.reportId',
                field: 'id',
                entityName: 'NotificationReport' as const,
                orderable: true,
            }),
            generateColumn<NotificationReport>({
                headerTitle: 'Modules.Admin.NotificationsList.table.reportName',
                field: 'name',
                entityName: 'NotificationReport' as const,
                orderable: true,
            }),
            {
                headerTitle: 'Modules.Admin.NotificationsList.table.status',
                entityName: 'NotificationReport' as const,
                orderable: true,
                orderByKey: 'is_paused',
                field: 'is_paused',
                RenderCell: StatusCell,
            },
            generateColumn<NotificationReport>({
                headerTitle: 'Modules.Admin.NotificationsList.table.frequency',
                field: 'interval',
                entityName: 'NotificationReport' as const,
                orderable: true,
            }),
            {
                headerTitle: 'Modules.Admin.NotificationsList.table.lastRun',
                entityName: 'NotificationReport' as const,
                orderable: true,
                orderByKey: 'last_run_at',
                field: 'last_run_at',
                RenderCell: ({ entity }: RenderColumnDataProps<NotificationReport>) => (
                    <Typography variant="body2">{formatDate(entity.updated_at)}</Typography>
                ),
            },
            {
                headerTitle: 'Modules.Admin.NotificationsList.table.nextRun',
                entityName: 'NotificationReport' as const,
                field: 'next_run_at',
                RenderCell: ({ entity }: RenderColumnDataProps<NotificationReport>) => (
                    <Typography variant="body2">{formatDate(entity.next_run_at)}</Typography>
                ),
            },
            {
                headerTitle: 'Modules.Admin.NotificationsList.table.recipients',
                entityName: 'NotificationReport' as const,
                field: 'email_addresses',
                RenderCell: ({ entity }: RenderColumnDataProps<NotificationReport>) => (
                    <Typography variant="body2">
                        {formatRecipients(entity.email_addresses)}
                    </Typography>
                ),
            },
        ],
        [],
    );

    return (
        <FullPageEntityTableWrapper
            title="Modules.Admin.NotificationsList.metaTitle"
            controls={useMemo(
                () => [
                    <CreateEntityButton<NotificationReport>
                        key="new-notification-btn"
                        entityName="NotificationReport"
                        title="Modules.Admin.NotificationsList.create.dialogTitle"
                        buttonLabel={
                            <Translate path="Modules.Admin.NotificationsList.create.newNotificationButton" />
                        }
                        submitLabel="Modules.Admin.NotificationsList.create.submitButton"
                        form={<NewNotificationForm getValidEmailCount={getValidEmailCount} />}
                        defaultData={createDefaults as unknown as Partial<NotificationReport>}
                        transformData={data =>
                            transformCreateValues(data as unknown as CreateNotificationFormValues)
                        }
                        redirectPath={PostAuthRoutes.Admin.NotificationDetail}
                        formOptions={{
                            defaultValues: createDefaults,
                            resolver: yupResolver(createSchema),
                        }}
                        permissions={[PermissionName.UniledportalNotificationCreate]}
                        maxWidth="md"
                    />,
                ],
                [createDefaults, getValidEmailCount, transformCreateValues],
            )}
        >
            <FilterableEntityTable
                key={paginationTag}
                tag={paginationTag}
                columns={columns}
                baseEntityName="NotificationReport"
                routeTemplate={PostAuthRoutes.Admin.NotificationDetail}
                textSearchColumns={textSearchCols}
                rendererType={FilterableEntityTableRendererType.Contained}
            />
        </FullPageEntityTableWrapper>
    );
};

const NewNotificationForm = ({
    getValidEmailCount,
}: {
    getValidEmailCount: (value: string) => number;
}) => {
    const t = useCommonTranslation();
    return (
        <Stack gap={2}>
            <ControlledFormInput
                name="name"
                render={({ field, fieldState }) => (
                    <TextField
                        label={t('Modules.Admin.NotificationsList.create.nameLabel')}
                        autoFocus
                        fieldState={fieldState}
                        {...field}
                    />
                )}
            />
            <ControlledFormInput
                name="interval"
                render={({ field, fieldState }) => (
                    <TextField
                        label={t('Modules.Admin.NotificationsList.create.intervalLabel')}
                        helperText={
                            fieldState.error?.message ??
                            t('Modules.Admin.NotificationsList.create.intervalHelper')
                        }
                        fieldState={fieldState}
                        {...field}
                    />
                )}
            />
            <ControlledFormInput
                name="email_subject"
                render={({ field, fieldState }) => (
                    <TextField
                        label={t('Modules.Admin.NotificationsList.create.emailSubjectLabel')}
                        fieldState={fieldState}
                        {...field}
                    />
                )}
            />
            <ControlledFormInput
                name="email_body"
                render={({ field, fieldState }) => (
                    <TextField
                        label={t('Modules.Admin.NotificationsList.create.emailBodyLabel')}
                        fieldState={fieldState}
                        multiline
                        minRows={4}
                        {...field}
                    />
                )}
            />
            <ControlledFormInput
                name="email_addresses"
                render={({ field, fieldState }) => (
                    <TextField
                        label={t('Modules.Admin.NotificationsList.create.recipientsLabel')}
                        helperText={
                            fieldState.error?.message ??
                            t('Modules.Admin.NotificationsList.create.recipientsCount', {
                                count: getValidEmailCount(field.value),
                            })
                        }
                        fieldState={fieldState}
                        multiline
                        minRows={4}
                        {...field}
                    />
                )}
            />
        </Stack>
    );
};

const setup: PageConfig = {
    id: 'NotificationsListPage',
    component: NotificationsListPage,
    systemSearch: {
        title: 'Modules.Admin.NotificationsList.metaTitle',
        description: 'Modules.Admin.NotificationsList.description',
        icon: notificationsIcon,
        type: 'page',
        aliases: undefined,
        route: PostAuthRoutes.Admin.NotificationsList,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.Admin.NotificationsList.metaTitle' },
        ],
        permissions: [PermissionName.Admin],
    },
};

export default setup;
